"use client"

import { useState, useRef, useEffect } from "react"
import { Plus, ChevronRight, FileText, AlertCircle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { UploadCompleteCard } from "@/components/upload-complete-card"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { uploadAndShareMultipleFiles } from '@/api/share-group'
import { useToast } from "@/hooks/use-toast"
import { copyToClipboard } from '@/utils/clipboard'
import { getDeviceId } from '@/utils/device'

export type UploadPhase = 'idle' | 'hashing' | 'uploading' | 'completing' | 'done' | 'error'

interface HeroSectionProps {
  onLoginClick?: () => void
  isLoggedIn?: boolean
}

export function HeroSection({ onLoginClick: _onLoginClick, isLoggedIn = false }: HeroSectionProps) {
  const { toast } = useToast()
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [codeMode, setCodeMode] = useState(false)
  const [codeValue, setCodeValue] = useState("")
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [retention, setRetention] = useState<'1d' | '7d' | '30d' | 'forever'>("1d")
  const [destroyAfterDownload, setDestroyAfterDownload] = useState(false)
  const [encryptedEnabled, setEncryptedEnabled] = useState(false)
  const [allowOthers, setAllowOthers] = useState(false)
  const [password] = useState("")
  const [extraDesc, setExtraDesc] = useState("")
  const [uploading, setUploading] = useState(false)
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [progress, setProgress] = useState(0)
  const [showHistory, setShowHistory] = useState(false)
  const [shareCode, setShareCode] = useState("")
  const [shareLink, setShareLink] = useState("")
  const [uploadError, setUploadError] = useState("")
  const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // ✅ 新增：速率和剩余时间状态
  const [uploadSpeed, setUploadSpeed] = useState("")
  const [_uploadRemaining, setUploadRemaining] = useState("")
  const [_currentFileName, setCurrentFileName] = useState("")

  // 上传防误关提示
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploading) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [uploading])

  useEffect(() => {
    // 未登录默认开启"下载后立刻销毁"
    if (!isLoggedIn) {
      setDestroyAfterDownload(true)
    }
  }, [isLoggedIn])

  const startUpload = async () => {
    if (!selectedFiles.length || uploading) return
    
    setUploading(true)
    setUploadPhase('hashing')
    setProgress(0)
    setUploadError("")
    
    // ✅ 清空之前的分享信息（后端init时会立即返回真实的）
    setShareLink('')
    setShareCode('')
    
    // 清除之前的定时器
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current)
      uploadTimerRef.current = null
    }
    
    try {
      // 计算有效期天数
      const getExpireDays = () => {
        switch (retention) {
          case '1d': return 1
          case '7d': return 7
          case '30d': return 30
          case 'forever': return 365
          default: return 1
        }
      }
      
      // 调用真实的上传API
      const result = await uploadAndShareMultipleFiles(selectedFiles, {
        shareName: selectedFiles.length === 1 ? selectedFiles[0].name : '文件传输',
        deviceId: getDeviceId(),  // 获取设备唯一标识
        expireDays: getExpireDays(),
        password: password || undefined,
        burnAfterRead: destroyAfterDownload,
        encrypted: encryptedEnabled,
        allowDownload: allowOthers,
        allowClone: allowOthers,
        allowTransfer: allowOthers,
        description: extraDesc || undefined,
        onPhaseChange: (phase) => {
          setUploadPhase(phase)
        },
        onProgress: (prog) => {
          setProgress(prog)
        },
        // ✅ 速率更新回调
        onSpeedUpdate: (_fileIndex, fileName, speed, remaining) => {
          setCurrentFileName(fileName)
          setUploadSpeed(speed)
          setUploadRemaining(remaining)
        },
        // ✅ 分享信息就绪回调 - 第一个文件上传完成后立即显示
        onShareInfoReady: (shareKey, downloadUrl) => {
          console.log('分享信息就绪:', { shareKey, downloadUrl })
          
          // 将相对路径转换为完整URL
          const fullUrl = downloadUrl?.startsWith('http') 
            ? downloadUrl 
            : `${window.location.origin}${downloadUrl}`
          
          // ✅ 立即设置分享链接和口令（统一使用 shareKey）
          setShareLink(fullUrl)
          setShareCode(shareKey || '')
          
          console.log('分享链接已设置:', fullUrl)
          console.log('口令已设置:', shareKey)
        },
      })
      
      // 设置分享信息
      // 将相对路径转换为完整URL
      const fullUrl = result.data.downloadUrl?.startsWith('http') 
        ? result.data.downloadUrl 
        : `${window.location.origin}${result.data.downloadUrl}`
      setShareLink(fullUrl)
      setShareCode(result.data.shareKey || '')
      setUploadPhase('done')
      setUploading(false)
      setProgress(100)
      
      // 显示成功提示
      toast({
        title: "上传成功！",
        description: `已上传 ${selectedFiles.length} 个文件`,
      })
      
    } catch (error) {
      console.error('Upload error:', error)
      const errorMsg = error instanceof Error ? error.message : '上传失败'
      setUploadError(errorMsg)
      setUploadPhase('error')
      setProgress(0)
      setUploading(false)
      
      // 显示错误提示
      toast({
        title: "上传失败",
        description: errorMsg,
        variant: "destructive",
      })
    }
  }

  useEffect(() => {
    return () => {
      if (uploadTimerRef.current) clearInterval(uploadTimerRef.current)
    }
  }, [])

  // 进度完成（done）后打开历史抽屉
  useEffect(() => {
    if (uploadPhase === 'done') {
      setShowHistory(true)
    }
  }, [uploadPhase])

  const onFiles = (files: FileList | null) => {
    if (!files || !files.length) return
    const incoming = Array.from(files)
    setSelectedFiles(prev => {
      const map = new Map<string, File>()
      prev.forEach(f => map.set(`${f.name}-${f.size}-${f.type}`, f))
      incoming.forEach(f => map.set(`${f.name}-${f.size}-${f.type}`, f))
      return Array.from(map.values())
    })
    setShowUploadForm(true)
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB", "TB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index)
      // 如果没有文件了，回到初始状态
      if (newFiles.length === 0) {
        setShowUploadForm(false)
      }
      return newFiles
    })
  }

  return (
    <div>
      <Card className="bg-transparent border-0 shadow-none max-w-[320px] overflow-visible">
        <div className="relative min-h-[120px]">
          {/* 初始 - 添加文件入口 */}
          <div
            role="button"
            onClick={() => fileInputRef.current?.click()}
            className={`transition-all duration-300 ease-out ${(!showUploadForm && !codeMode) ? 'opacity-100 translate-y-0 scale-100 relative pointer-events-auto' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none absolute inset-0'} group flex items-center justify-between bg-white rounded-full px-4 py-3 cursor-pointer ${isDragActive ? 'ring-2 ring-yellow-400' : 'shadow-[0_8px_24px_rgba(0,0,0,0.08)]'}`}
            onDragEnter={() => setIsDragActive(true)}
            onDragOver={(e) => { e.preventDefault(); setIsDragActive(true) }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragActive(false); onFiles(e.dataTransfer.files) }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click() }}
              className="flex items-center gap-3 cursor-pointer group/btn text-left"
            >
              <Plus className="w-6 h-6 text-gray-900 flex-shrink-0" />
              <div className="leading-tight">
                <div className="text-gray-900 font-bold text-lg hover:text-yellow-600 transition-colors">添加文件</div>
                <div 
                  onClick={(e) => {
                    e.stopPropagation()
                    folderInputRef.current?.click()
                  }}
                  className="text-xs text-yellow-600 font-medium hover:underline hover:text-yellow-700 mt-0.5 cursor-pointer"
                  title="点击选择整个文件夹"
                >
                  + 添加文件夹
                </div>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
            <input
              ref={folderInputRef}
              type="file"
              multiple
              // @ts-ignore
              webkitdirectory=""
              directory=""
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCodeMode(true) }}
              className="rounded-full px-4 py-2 bg-gray-100 text-gray-800 text-sm transition-colors hover:bg-black hover:text-white"
            >
              口令取件
            </button>
          </div>

          {/* 口令输入态 */}
          <div className={`transition-all duration-300 ease-out ${(!showUploadForm && codeMode) ? 'opacity-100 translate-y-0 scale-100 relative pointer-events-auto' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none absolute inset-0'} flex items-center bg-white rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.08)] px-4 py-2 border border-gray-900/80`}>
            <Input
              autoFocus={codeMode}
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value.trim())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const code = codeValue.trim()
                  if (!code) return
                  if (!/^[a-zA-Z0-9_-]{4,32}$/.test(code)) {
                    toast({
                      title: "口令格式错误",
                      description: "请输入有效的提取口令",
                      variant: "destructive",
                    })
                    return
                  }
                  window.location.href = `/download/group/${code}`
                }
              }}
              placeholder="输入提取口令按回车获取"
              className="border-none outline-none ring-0 focus-visible:ring-0 h-8 px-0 bg-transparent text-sm font-mono tracking-wider"
            />
            <button
              type="button"
              aria-label="close"
              onClick={() => { setCodeMode(false); setCodeValue("") }}
              className="ml-2 w-7 h-7 rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          {/* 上传表单态 / 上传中 */}
          <div
            className={`transition-all duration-300 ease-out ${showUploadForm ? 'opacity-100 translate-y-0 scale-100 relative pointer-events-auto' : 'opacity-0 translate-y-2 scale-95 pointer-events-none absolute inset-0'} bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-3 space-y-2 overflow-hidden ${isDragActive ? 'ring-2 ring-yellow-400' : ''}`}
            onDragEnter={() => setIsDragActive(true)}
            onDragOver={(e) => { e.preventDefault(); setIsDragActive(true) }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragActive(false); onFiles(e.dataTransfer.files) }}
          >
            {(uploading || uploadPhase === 'done') ? (
              <div className="flex items-center justify-center py-2">
                <UploadCompleteCard 
                  progress={progress} 
                  embedded 
                  shareLink={shareLink}
                  shareCode={shareCode}
                  fileCount={selectedFiles.length}
                  fileSize={formatBytes(selectedFiles.reduce((s,f)=>s+f.size,0))}
                  speed={uploadSpeed || '计算中...'}
                  uploadPhase={uploadPhase}
                  onTransferAgain={() => { 
                    if (uploadTimerRef.current) {
                      clearInterval(uploadTimerRef.current)
                      uploadTimerRef.current = null
                    }
                    setSelectedFiles([])
                    setShowUploadForm(false)
                    setProgress(0)
                    setUploading(false)
                    setUploadPhase('idle')
                    setUploadError('')
                    setUploadSpeed('')
                    setUploadRemaining('')
                    setCurrentFileName('')
                  }}
                  onCopy={async () => {
                    if (!shareLink && !shareCode) return
                    const copyContent = shareCode ? `${shareLink} 口令: ${shareCode}` : shareLink
                    const success = await copyToClipboard(copyContent)
                    if (success) {
                      toast({
                        title: "复制成功！",
                        description: "分享链接和口令已复制到剪贴板",
                      })
                    } else {
                      toast({
                        title: "复制失败",
                        description: "请手动复制链接",
                        variant: "destructive",
                      })
                    }
                  }}
                />
              </div>
            ) : (
              <>
            {/* 标题 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900">文件传输</h3>
                {selectedFiles.length > 0 && (
                  <span className="text-[10px] bg-yellow-400 text-gray-900 font-bold px-1.5 py-0.5 rounded-full">
                    {selectedFiles.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {selectedFiles.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-1.5 text-[11px] text-gray-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setSelectedFiles([])
                      setShowUploadForm(false)
                      setUploadError("")
                    }}
                  >
                    清空
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-5 w-5 rounded-full bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                  title="继续添加文件"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              {/* 隐藏文件选择（表单内继续添加） */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => onFiles(e.target.files)}
              />
            </div>

            {/* 持久错误提示条 */}
            {uploadError && (
              <div className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                <div className="flex items-center gap-1.5 min-w-0">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  <span className="truncate">{uploadError}</span>
                </div>
                <button 
                  type="button" 
                  onClick={() => setUploadError("")}
                  className="text-red-400 hover:text-red-600 ml-2 text-sm leading-none font-bold"
                  aria-label="关闭错误提示"
                >
                  ×
                </button>
              </div>
            )}

            {/* 文件信息（多文件列表） */}
            <div className="space-y-1">
              <ul className="max-h-40 overflow-auto pr-1 space-y-1">
                {selectedFiles.length ? selectedFiles.map((f, idx) => (
                  <li key={`${f.name}-${f.size}-${idx}`} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">{f.name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] text-gray-500">{formatBytes(f.size)}</span>
                      <button
                        type="button"
                        aria-label="remove"
                        className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 flex items-center justify-center"
                        onClick={() => removeFile(idx)}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                )) : (
                  <li className="text-xs text-gray-400">未选择文件</li>
                )}
              </ul>
              <div className="text-right">
                <p className="text-xs text-gray-400">
                  {selectedFiles.length ? `共 ${formatBytes(selectedFiles.reduce((s,f)=>s+f.size,0))}` : '未选择文件'}
                </p>
              </div>
            </div>

            {/* 加密传输 */}
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-900">加密传输</span>
                <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded">PRO</span>
              </div>
              <Switch 
                checked={encryptedEnabled}
                onCheckedChange={setEncryptedEnabled}
                disabled={true}
              />
            </div>

            {/* 允许他人 */}
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-900">允许他人</span>
                <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded">PRO</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-gray-500">下载、转存及克隆</span>
                <Switch 
                  checked={allowOthers}
                  disabled={true}
                  onCheckedChange={setAllowOthers}
                />
              </div>
            </div>

            {/* 下载后销毁 */}
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-gray-900">下载后销毁</span>
              <Switch 
                checked={destroyAfterDownload}
                onCheckedChange={setDestroyAfterDownload}
              />
            </div>

            {/* 有效期 */}
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-gray-900">有效期</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-xs text-gray-900 hover:text-gray-600">
                    <span>
                      {retention === '1d' && '1 天'}
                      {retention === '7d' && '7 天'}
                      {retention === '30d' && '30 天'}
                      {retention === 'forever' && '永久 PRO'}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => setRetention('1d')}>1 天</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRetention('7d')}>7 天</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRetention('30d')}>30 天</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRetention('forever')}>永久 PRO</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 补充描述 */}
            <div className="py-1">
              <span className="text-xs text-gray-900">补充描述</span>
              <Input
                value={extraDesc}
                onChange={(e) => setExtraDesc(e.target.value)}
                placeholder="填写补充描述（可选）"
                className="mt-1 h-9 text-xs"
              />
            </div>

            {/* 用户协议 */}
            <div className="flex items-start gap-1.5 pt-0.5">
              <Checkbox id="terms" className="mt-0.5 h-3 w-3" checked={true} />
              <label htmlFor="terms" className="text-[12px] text-gray-500 leading-snug cursor-pointer">
                我已阅读并理解《用户协议》《隐私协议》《平台使用协议》等文档且同意遵守其中规定
              </label>
            </div>

            {/* 开始上传按钮 */}
            <Button
              className="w-full h-10 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full font-medium text-sm shadow-sm flex items-center justify-center gap-2"
              onClick={startUpload}
              disabled={!selectedFiles.length || uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>准备上传...</span>
                </>
              ) : (
                '开始上传'
              )}
            </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* 右侧传输预览抽屉 */}
      <Sheet open={showHistory} onOpenChange={setShowHistory}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] p-0">
          <SheetTitle className="sr-only">传输预览</SheetTitle>
          {/* 顶部工具栏 */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-white/70 backdrop-blur-sm">
            <Button
              variant="ghost"
              className="h-8 px-3 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
              onClick={() => setShowHistory(false)}
            >
              关闭
            </Button>
          </div>

          {/* 内容区域 */}
          <div className="px-6 py-6 space-y-5">
            <h2 className="text-2xl font-extrabold text-gray-900">传输预览</h2>
            <div className="text-sm text-gray-600">
              {selectedFiles.length} 个文件 | {formatBytes(selectedFiles.reduce((s,f)=>s+f.size,0))} | 结束时间 {new Date().toLocaleString()}
            </div>

            <div className="rounded-xl border border-gray-100 overflow-hidden bg-white">
              <ul className="divide-y divide-gray-100">
                {selectedFiles.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="px-5 py-4 flex items-center gap-3 hover:bg-gray-50">
                    <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{f.name}</p>
                      <p className="text-xs text-gray-500">{formatBytes(f.size)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      
    </div>
  )
}
